import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { notificacoesService } from '../../services/api';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffM = Math.floor(diffMs / 60000);
  if (diffM < 1) return 'Agora';
  if (diffM < 60) return `${diffM} min`;
  const diffH = Math.floor(diffM / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export default function NotificacoesBell() {
  const [list, setList] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchNotificacoes = async () => {
    try {
      setLoading(true);
      const res = await notificacoesService.listar();
      if (res?.data?.success) {
        setList(res.data.data || []);
        setNaoLidas(res.data.naoLidas ?? 0);
      }
    } catch (e) {
      console.error('Erro ao carregar notificações:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotificacoes();
    const t = setInterval(fetchNotificacoes, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onFocus = () => fetchNotificacoes();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const marcarLida = async (id) => {
    try {
      await notificacoesService.marcarLida(id);
      setList((prev) => prev.map((n) => (n._id === id ? { ...n, lida: true } : n)));
      setNaoLidas((n) => Math.max(0, n - 1));
    } catch (e) {
      console.error(e);
    }
  };

  const marcarTodasLidas = async () => {
    try {
      await notificacoesService.marcarTodasLidas();
      setList((prev) => prev.map((n) => ({ ...n, lida: true })));
      setNaoLidas(0);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 text-neutral-600 hover:text-neutral-900 rounded-full hover:bg-neutral-100 transition-colors"
        title="Notificações"
      >
        <span className="text-xl">🔔</span>
        {naoLidas > 0 && (
          <span className="absolute top-0 right-0 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white px-1">
            {naoLidas > 99 ? '99+' : naoLidas}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border border-neutral-200 z-50 max-h-[70vh] flex flex-col">
          <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-200">
            <span className="font-medium text-neutral-900">Notificações</span>
            {naoLidas > 0 && (
              <button
                type="button"
                onClick={marcarTodasLidas}
                className="text-xs text-primary-600 hover:text-primary-800"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {loading && list.length === 0 ? (
              <p className="p-4 text-sm text-neutral-500">Carregando...</p>
            ) : list.length === 0 ? (
              <p className="p-4 text-sm text-neutral-500">Nenhuma notificação.</p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {list.map((n) => (
                  <li
                    key={n._id}
                    className={`px-3 py-2 ${!n.lida ? 'bg-primary-50/50' : ''}`}
                  >
                    {n.tipo === 'OS_OPERACIONAL' && n.dados?.serviceOrderId ? (
                      <Link
                        to={`/frota/ordens-servico?destaque=${n.dados.serviceOrderId}`}
                        onClick={() => {
                          setOpen(false);
                          if (!n.lida) marcarLida(n._id);
                        }}
                        className="block"
                      >
                        <p className="font-medium text-neutral-900 text-sm">{n.titulo}</p>
                        <p className="text-xs text-neutral-600 mt-0.5 line-clamp-2">{n.mensagem}</p>
                        <p className="text-xs text-neutral-400 mt-1">{formatDate(n.createdAt)}</p>
                      </Link>
                    ) : (
                      <div
                        className="block cursor-pointer"
                        onClick={() => !n.lida && marcarLida(n._id)}
                        onKeyDown={(e) => e.key === 'Enter' && !n.lida && marcarLida(n._id)}
                        role="button"
                        tabIndex={0}
                      >
                        <p className="font-medium text-neutral-900 text-sm">{n.titulo}</p>
                        <p className="text-xs text-neutral-600 mt-0.5 line-clamp-2">{n.mensagem}</p>
                        <p className="text-xs text-neutral-400 mt-1">{formatDate(n.createdAt)}</p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
